import React, { useEffect, useState } from "react";
import NxtbnDrawer from "../../drawer";
import useApi from "../../../api";
import { InputField, NXForm } from "../../common";
import { NXUser } from "../../../icons";
import { toast } from "react-toastify";
import SelectStyled from "../../Select";
import enumChoice, { getEnumItem } from "../../../enum";
import { gql, useQuery } from "@apollo/client";

interface FormProps {
  edit?: any;
  isOpen: boolean;
  onClose: () => void;
}

const GET_USER_DETAILS = gql`
  query MyQuery($id: Int!) {
    user(id: $id) {
      isStaff
      isStoreAdmin
      isStoreStaff
      isSuperuser
      lastName
      firstName
      phoneNumber
      email
      username
      role
    }
  }
`;

function UseEditForm({ edit, isOpen, onClose }: FormProps) {
  const api = useApi();
  const [formData, setFormData] = useState<any>({});
  const [errorData, setErrorData] = useState<any>();
  const [avatar, setAvatar] = useState<File>();

  const { data, loading, error, refetch } = useQuery(GET_USER_DETAILS, {
    variables: { id: edit },
    notifyOnNetworkStatusChange: true,
    skip: !edit,
  });

  useEffect(() => {
    if (data?.user) {
      setFormData({
        first_name: data.user.firstName || "",
        last_name: data.user.lastName || "",
        email: data.user.email || "",
        phone_number: data.user.phoneNumber || "",
        role: data.user.role || "",
        is_store_admin: data.user.isStoreAdmin || false,
        is_store_staff: data.user.isStoreStaff || false,
      });
    }
  }, [data, edit]); // Add `edit` to dependencies
  

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: any, value: any) => {
    setFormData((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, files } = e.target;
    if (files && files.length > 0) {
      const selectedFile = files[0];
      setAvatar(selectedFile);
    }
  };

  const onSubmit = (e: any) => {
    e.preventDefault();
    const updatedFormData = { ...formData, avatar: avatar || null };
    api
      .updateUser(edit, updatedFormData)
      .then(() => {
        toast.success("Successfully updated user.");
        onClose();
      })
      .catch((error: any) => {
        setErrorData(error.response.data);
        toast.error("Error updating user.");
      });
  };

  return (
    <NxtbnDrawer
      size="md"
      isOpen={isOpen}
      onClose={() => {
        setFormData({});
        onClose();
      }}
      title="Update User"
    >
      <NXForm className="p-4" onSubmit={onSubmit} title="Update User Information" nonFieldError={{}}>
        <div className="mb-6">
          <label htmlFor="avatar" className="text-sm font-medium text-gray-700 flex justify-center">
            {avatar ? (
              <img src={URL.createObjectURL(avatar)} alt="" className="h-[150px] w-[150px] object-cover border rounded-full" />
            ) : (
              <NXUser className="h-[150px] w-[150px] object-cover border rounded-full" />
            )}
          </label>
          <InputField id="avatar" type="file" name="avatar" onChange={handleImageUpload} className="hidden" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <InputField type="text" name="first_name" placeholder="First Name" value={formData.first_name || ''} onChange={handleInputChange} />
          <InputField type="text" name="last_name" placeholder="Last Name" value={formData.last_name || ''} onChange={handleInputChange} />
          <InputField type="email" name="email" placeholder="Email" value={formData.email || ''} onChange={handleInputChange} />
          <InputField type="text" name="phone_number" placeholder="Phone Number" value={formData.phone_number || ''} onChange={handleInputChange} />
          <InputField type="text" name="role" placeholder="Marketing Manager" value={formData.role || ''} onChange={handleInputChange} />
        </div>

        <div className="flex items-center gap-4">
          <label className="flex items-center space-x-2">
            <input type="checkbox" name="is_store_admin" checked={formData.is_store_admin} onChange={(e) => handleSelectChange(e.target.name, e.target.checked)} />
            <span>Is Store Admin</span>
          </label>
          <label className="flex items-center space-x-2">
            <input type="checkbox" name="is_store_staff" checked={formData.is_store_staff} onChange={(e) => handleSelectChange(e.target.name, e.target.checked)} />
            <span>Is Store Staff</span>
          </label>
        </div>

        <div className="flex justify-end mt-4">
          <button type="submit" className="px-4 py-2 text-white bg-green-600 hover:bg-green-500 rounded-md">
            Update User
          </button>
        </div>
      </NXForm>
    </NxtbnDrawer>
  );
}

export default UseEditForm;
